import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, CircularProgress, Grid, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { BarChartRounded, InfoOutlined, PeopleOutline, RefreshRounded, WebOutlined, VisibilityOutlined } from '@material-ui/icons';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import CmtCard from '@coremat/CmtCard';
import axios from 'services/auth/jwt/config';
import { getMyAccess } from 'services/adminAccess';
import { ChartCard, EmptyState, Metric } from './dashboardCharts';
import useStyles from './dashboardStatistics.style';

const number = value => new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 0 }).format(value);
const VisitsChart = ({ rows, daily = false, onSelectMonth }) => {
  const classes = useStyles();
  const theme = useTheme();
  return <div className={classes.chart} role="img" aria-label={daily ? 'نمودار بازدید روزانه' : 'نمودار بازدید ماهانه'}>
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={rows} margin={{ top: 16, right: 12, left: 0, bottom: daily ? 4 : 14 }}
        onClick={event => { if (!daily && event && event.activePayload && event.activePayload.length) onSelectMonth(event.activePayload[0].payload.key); }}>
        <CartesianGrid vertical={false} stroke={theme.palette.divider} strokeDasharray="3 5" />
        <XAxis dataKey={daily ? 'day' : 'label'} tickFormatter={daily ? number : undefined}
          axisLine={false} tickLine={false} angle={daily ? 0 : -25} textAnchor={daily ? 'middle' : 'end'}
          height={daily ? 30 : 64} minTickGap={18} />
        <YAxis allowDecimals={false} tickFormatter={number} axisLine={false} tickLine={false} width={54} />
        <Tooltip formatter={number} labelFormatter={(label, payload) => payload && payload.length ? payload[0].payload.label : label} />
        <Legend />
        <Line dataKey="totalVisits" name="کل بازدیدها" stroke={theme.palette.primary.main} strokeWidth={2} dot={rows.length === 1 ? { r: 4 } : false} isAnimationActive={false} />
        <Line dataKey="uniqueVisitors" name="بازدیدکنندگان یکتا" stroke={theme.palette.secondary.main} strokeWidth={2} dot={rows.length === 1 ? { r: 4 } : false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  </div>;
};

const VisitsTable = ({ rows, label, route = false }) => {
  const classes = useStyles();
  return <div className={classes.tableScroll}>
    <Table size="small" stickyHeader aria-label={label}>
      <TableHead><TableRow>
        <TableCell>{route ? 'مسیر' : 'تاریخ'}</TableCell>
        <TableCell align="right">کل بازدیدها</TableCell>
        <TableCell align="right">بازدیدکنندگان یکتا</TableCell>
      </TableRow></TableHead>
      <TableBody>{rows.map(row => <TableRow key={row.route || row.key || row.date}>
        <TableCell>{route ? <span dir="ltr" style={{ display: 'inline-block', overflowWrap: 'anywhere' }}>{row.route}</span> : row.label}</TableCell>
        <TableCell align="right">{number(row.totalVisits)}</TableCell>
        <TableCell align="right">{number(row.uniqueVisitors)}</TableCell>
      </TableRow>)}</TableBody>
    </Table>
  </div>;
};

export default function DashboardVisits() {
  const classes = useStyles();
  const theme = useTheme();
  const token = useSelector(({ auth }) => auth.token);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: 'loading', data: null, token: null });
  const [selectedMonth, setSelectedMonth] = useState('');
  const refresh = () => setAttempt(value => value + 1);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', data: null, token });
    if (!token) return () => { cancelled = true; };
    const load = async () => {
      try {
        if (!await getMyAccess('UserManager')) {
          if (!cancelled) setState({ status: 'denied', data: null, token });
          return;
        }
        if (cancelled) return;
        const { data } = await axios.get('/visits/statistics');
        if (!cancelled) {
          setSelectedMonth(data.currentMonthKey);
          setState({ status: 'ready', data, token });
        }
      } catch {
        if (!cancelled) setState({ status: 'error', data: null, token });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token, attempt]);

  if (state.status === 'loading' || state.token !== token) return <CmtCard className={classes.card}>
    <div className={classes.status} role="status"><CircularProgress size={28} /><Typography>در حال دریافت آمار بازدید…</Typography></div>
  </CmtCard>;
  if (state.status === 'denied') return <CmtCard className={classes.card}><div className={classes.status}>
    <PeopleOutline color="disabled" fontSize="large" /><Typography>برای مشاهده آمار بازدید، دسترسی مدیریت کاربران لازم است.</Typography>
  </div></CmtCard>;
  if (state.status === 'error') return <CmtCard className={classes.card}><div className={classes.status}>
    <InfoOutlined color="error" fontSize="large" /><Typography role="alert">دریافت آمار بازدید ممکن نشد.</Typography>
    <Button onClick={refresh} variant="outlined" color="primary" startIcon={<RefreshRounded />}>تلاش دوباره</Button>
  </div></CmtCard>;

  const { data } = state;
  const month = data.months.find(item => item.key === selectedMonth) || data.months[data.months.length - 1];
  const updatedAt = new Intl.DateTimeFormat('fa-IR', { timeZone: 'Asia/Tehran', dateStyle: 'medium', timeStyle: 'short' }).format(new Date(data.generatedAt));
  return <section aria-label="آمار بازدید">
    <div className={classes.pageHeader}>
      <div className={classes.heading}>
        <div className={classes.headingIcon}><BarChartRounded /></div>
        <div><Typography component="h2" variant="h2">آمار بازدید</Typography>
          <Typography className={classes.subtitle}>آخرین به‌روزرسانی: {updatedAt} (تهران)</Typography></div>
      </div>
      <Button onClick={refresh} className={classes.refreshButton} variant="outlined" color="primary" startIcon={<RefreshRounded />}>به‌روزرسانی بازدیدها</Button>
    </div>
    <Grid container spacing={6}>
      <Metric title="کل بازدیدها" value={number(data.totalVisits)} detail="از شروع جمع‌آوری آمار؛ هر بار مشاهده صفحه" icon={<VisibilityOutlined />} color={theme.palette.primary.main} />
      <Metric title="بازدیدکنندگان یکتا" value={number(data.uniqueVisitors)} detail="مرورگرهای متمایز از شروع جمع‌آوری آمار" icon={<PeopleOutline />} color={theme.palette.secondary.main} />
      <Metric title="مسیرهای بازدیدشده" value={number(data.totalRoutes)} detail="مسیرهای متمایز برنامه کاربران" icon={<WebOutlined />} color={theme.palette.success.main} />
      <Grid item xs={12} lg={6}><ChartCard title="بازدید در ۱۲ ماه اخیر" subtitle="برای انتخاب جزئیات روزانه، روی نمودار ماه کلیک کنید.">
        {data.months.some(row => row.totalVisits > 0) ? <VisitsChart rows={data.months} onSelectMonth={setSelectedMonth} /> : <EmptyState>هنوز بازدیدی در این بازه ثبت نشده است.</EmptyState>}
        <details className={classes.tableDetails}><summary className={classes.summary}>نمایش جدول بازدید ماهانه</summary>
          <VisitsTable rows={data.months} label="بازدید ماهانه" /></details>
      </ChartCard></Grid>
      <Grid item xs={12} lg={6}><ChartCard title={`بازدید روزانه ${month.label}`} subtitle="کل بازدیدها و مرورگرهای یکتا در هر روز" action={
        <TextField className={classes.monthSelect} id="visit-statistics-month" select label="ماه بازدید" variant="outlined" size="small"
          value={month.key} onChange={event => setSelectedMonth(event.target.value)} SelectProps={{ native: true }}>
          {data.months.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
        </TextField>}>
        <div className={classes.chartMeta}><span>{number(month.totalVisits)} بازدید</span><span>{number(month.uniqueVisitors)} مرورگر یکتا در ماه</span></div>
        {month.totalVisits > 0 ? <VisitsChart rows={month.days} daily /> : <EmptyState>در این ماه بازدیدی ثبت نشده است.</EmptyState>}
        <details className={classes.tableDetails}><summary className={classes.summary}>نمایش جدول بازدید روزانه</summary>
          <VisitsTable rows={month.days} label="بازدید روزانه" /></details>
      </ChartCard></Grid>
      <Grid item xs={12}><ChartCard title="بازدید به تفکیک مسیر" subtitle="۱۰۰ مسیر پربازدید از شروع جمع‌آوری آمار؛ شناسه‌های متغیر هر مسیر با هم محاسبه می‌شوند.">
        {data.routes.length > 0 ? <VisitsTable rows={data.routes} label="بازدید مسیرها" route /> : <EmptyState>هنوز بازدیدی ثبت نشده است.</EmptyState>}
      </ChartCard></Grid>
    </Grid>
    <div className={classes.footer}><InfoOutlined /><Typography>
      فقط بازدید برنامه کاربران ثبت می‌شود؛ پنل مدیریت و مینی‌اپ‌ها محاسبه نمی‌شوند. هر بار بارگذاری یا تغییر مسیر یک بازدید است.
      یکتایی بر اساس مرورگر است؛ پاک کردن داده‌های مرورگر یا استفاده از دستگاه دیگر، بازدیدکننده جدید محسوب می‌شود.
      آمار ماهانه و روزانه به وقت تهران است؛ بازدیدهای پیش از شروع جمع‌آوری در دسترس نیست.
    </Typography></div>
  </section>;
}
